'use client'

import type { ChildrenProps } from '@snowball-tech/common/types'

import Link from '@snowball-tech/common/components/Link'
import useLogger from '@snowball-tech/common/hooks/useLogger'
import { Typography } from '@snowball-tech/fractal/dist/src/components/Typography'

import isEmpty from 'lodash/fp/isEmpty'

import { SNOWBALL_MEDIA_DOMAIN } from '@/constants'
import useApi from '@/hooks/useApi'

type Props = {
  editionUuid: string
  href: string
  sponsorBlockId?: string
} & ChildrenProps
export default function Component({
  children,
  editionUuid,
  href,
  sponsorBlockId,
}: Props) {
  const endpoints = useApi()

  const logger = useLogger()

  const trackLinkClick = () => {
    // Fire and forget the analytics tracking call.
    endpoints.analytics.track.links
      .click(editionUuid, href, 'media', sponsorBlockId)
      .catch((trackingError) => {
        const error = trackingError as Error

        logger.warn(
          `[EDITION] An error occurred when tracking the click on the link "${href}" for "${editionUuid}": "${error.message}"`,
          { editionUuid, href, sponsorBlockId },
          error,
        )
      })
  }

  let cleanHref = href

  try {
    const hrefUrl = new URL(href)

    if (
      href.includes(`redirect.${SNOWBALL_MEDIA_DOMAIN}`) &&
      hrefUrl.searchParams.has('to') &&
      !isEmpty(hrefUrl.searchParams.get('to'))
    ) {
      cleanHref = decodeURIComponent(hrefUrl.searchParams.get('to')!) || href
    }
  } catch (error) {
    const actualError = error as Error
    logger.warn(
      `[EDITION] An error occurred when cleaning the href "${href}" for "${editionUuid}": "${actualError.message}"`,
      { editionUuid, href },
      actualError,
    )

    cleanHref = ''
  }

  return isEmpty(cleanHref) ? (
    <Typography>{children}</Typography>
  ) : (
    <Link
      /* Change this if you update styles of links in Fractal. */
      className="font-weight-median underline"
      href={cleanHref || href}
      rel="noopener noreferrer"
      target="_blank"
      onClick={trackLinkClick}
    >
      {children}
    </Link>
  )
}
